import React from 'react';
import TabContentWrapper from '../TabContentWrapper';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import {
   ControlButton,
   TscButtonType,
   TscThemeName,
   TscThemeNames, TscStyles
} from '@techsmith/tsc-cloud-style-guide';
import dayjs from 'dayjs';
import cssConstants from '../../../constants/cssConstants';
import {StyledWarning} from '../../util/StyledElements';
import quizApi from '../../../service/quizApi';
import {IWorkerState} from '../../../model/workerState';
import {IQuizData} from '../../../model/quizModel';
import AlertModalSpinner from '../../util/AlertModalSpinner';
import '../../../static/css/modal.less';
import withMemoizedContexts from '../../../context/contextContainerHoC';
import {useTscTheme} from '../../../context/themeProvider';
import {quizDataStore, updateQuizData} from '../../../context/quizDataProvider';
import {mediaStore} from '../../../context/mediaProvider';
import deepEqual from 'deep-equal';
import {AlertModalPortal, BasicModalPortal} from '../../util/ModalPortalHelper';
import Constants from '../../../constants/Constants';

const EmptyStateWrapper = styled.div`
   display: flex;
   flex-direction: column;
   justify-content: center;
   align-items: center;
   min-height: 6.75rem;
   margin-top: 1rem;
`;
EmptyStateWrapper.displayName = 'empty-state-wrapper';

const EmptyStateHeader = styled.h3`
   margin: 0;
`;
EmptyStateHeader.displayName = 'empty-state-header';

const EmptyStateText = styled.span`
   margin: 1rem;
`;
EmptyStateText.displayName = 'empty-state-text';

const QuizDisplayWrapper = styled.div`
   display: flex;
   flex-direction: column;
`;

const QuizTitleSpan = styled.span`
   margin: 1rem 0 0.25rem 0;
   font-weight: ${cssConstants.semiBoldWeight};
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
`;
QuizTitleSpan.displayName = 'quiz-title-span';

const ButtonGroup = styled.div`
   margin-top: 1rem;

   > Button {
      margin-right: 1rem;
   }

   @media screen and (max-width: ${cssConstants.contentBreakpoint}px) {
      display: flex;
      flex-direction: column;

      > Button {
         margin-top: 1rem;
      }
   }
`;

const StyledIFrame = styled.iframe`
   height: 40rem;
   width: 100%;
   box-sizing: border-box;
   border: none;
`;

const QuizRetirementAnnouncementContainer = styled.div<{theme: TscThemeNames}>`
   display: flex;
   align-items: center;
   justify-content: space-between;
   padding: .5rem;
   border-radius: ${TscStyles.border.radius.md};
   border: 1px solid ${TscStyles.color.message.caution};
   background-color: ${props => props.theme === TscThemeName.dawn ? TscStyles.color.ui.dawn.lightMedium : TscStyles.color.ui.dusk.mediumDark};
`;

const NewQuizRemoveMessage = styled.div`
   margin: 0 0 .5rem 0;
   font-weight: ${TscStyles.font.weight.semibold};
`;

enum displayStates {
   initial,
   loading,
   emptyState,
   showQuiz
}

enum modalStates {
   none,
   viewResults,
   edit,
   removeQuiz
}

export const QuizzingTabBase: React.FC<IQuizzingTabProps & IStateMappedProps> = ({initialIsWorking, initialHasError, Id, HasBeenPublished, Title, CreateDate, ResultsUrl, EditUrl, QuestionSets, Hash, IsEncoding, QuizId}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();

   const [state, setState] = React.useState({
      isWorking: initialIsWorking,
      hasError: initialHasError,
      errorText: '',
      currentDisplay: displayStates.initial,
      currentModal: modalStates.none
   } as IQuizzingTabState);

   const updateState = (updatedValues: Partial<IQuizzingTabState>): void => {
      setState(prevState => Object.assign({}, prevState, updatedValues));
   };

   const fetchAndUpdateQuizData = async (): Promise<IQuizData> => {
      try {
         const updatedQuizData = await quizApi.getQuizData(Hash);
         updateQuizData(updatedQuizData);
         updateState({isWorking: false, hasError: false, errorText: ''});
         return updatedQuizData;
      } catch (e) {
         !IsEncoding && updateState({isWorking: false, hasError: true, errorText: t('quizzing.quizDataFetchError')});
      }
      return null;
   };

   const updateDisplayState = () => {
      if (!Id.length) {
         updateState({currentDisplay: displayStates.loading});
      } else {
         updateState({
            currentDisplay: HasBeenPublished ?
               displayStates.showQuiz :
               displayStates.emptyState
         });
      }
   };

   React.useEffect(() => {
      updateDisplayState();
   }, [Id, HasBeenPublished]);

   React.useEffect(() => {
      void fetchAndUpdateQuizData();
   }, [QuizId]);

   const handleViewResultsClick = () => {
      updateState({currentModal: modalStates.viewResults});
   };

   const handleEditClick = () => {
      updateState({currentModal: modalStates.edit});
   };

   const handleRemoveQuizClick = () => {
      updateState({currentModal: modalStates.removeQuiz});
   };

   const closeModals = async () => {
      const updatedQuizData = await fetchAndUpdateQuizData();

      if (!deepEqual(QuestionSets, updatedQuizData.QuestionSets)) {
         window.location.reload();
         return;
      }
      updateState({currentModal: modalStates.none});
   };

   const removeQuiz = async () => {
      try {
         updateState({isWorking: true});
         await quizApi.removeQuiz(Hash);
         await fetchAndUpdateQuizData();
         updateState({isWorking: false, currentModal: modalStates.none, hasError: false, errorText: ''});
         window.location.reload();
      } catch (e) {
         updateState({isWorking: false, hasError: true, errorText: t('general.errorText')});
      }
   };

   const initialDisplay: React.JSX.Element = (
      <EmptyStateWrapper />
   );

   const loadingDisplay: React.JSX.Element = (
      <EmptyStateWrapper>
         <EmptyStateText>
            {t('quizzing.loadingQuizDataText')}
         </EmptyStateText>
      </EmptyStateWrapper>
   );

   const emptyStateDisplay: React.JSX.Element = (
      <EmptyStateWrapper>
         <EmptyStateHeader>
            {t('quizzing.noQuiz')}
         </EmptyStateHeader>
      </EmptyStateWrapper>
   );

   const quizDisplay: React.JSX.Element = (
      <QuizDisplayWrapper>
         <QuizTitleSpan>
            {Title}
         </QuizTitleSpan>
         <span>
            {t('quizzing.createdOn', {date: dayjs(CreateDate).format('MM/DD/YYYY')})}
         </span>
         <ButtonGroup>
            <ControlButton
               themeName={theme}
               buttonType={TscButtonType.secondary}
               label={t('quizzing.viewResultsButton')}
               onClick={() => handleViewResultsClick()}
               testId="quizzing-tab-view-results-button"
            />
            <ControlButton
               themeName={theme}
               buttonType={TscButtonType.secondary}
               label={t('quizzing.editButton')}
               onClick={() => handleEditClick()}
               testId="quizzing-tab-edit-quiz-button"
            />
            <ControlButton
               themeName={theme}
               buttonType={TscButtonType.secondary}
               label={t('quizzing.removeQuizButton')}
               onClick={() => handleRemoveQuizClick()}
               testId="quizzing-tab-remove-quiz-secondary-button"
            />
         </ButtonGroup>
      </QuizDisplayWrapper>
   );

   const viewResultsModal = (
      <BasicModalPortal
         title={t('quizzing.quizResultsModalTitle')}
         width="68rem"
         onClose={closeModals}
         themeName={theme}
         isLoading={state.isWorking || initialIsWorking}
      >
         <StyledIFrame src={ResultsUrl} />
      </BasicModalPortal>
   );

   const editQuizModal = (
      <BasicModalPortal
         title={t('quizzing.editQuizModalTitle')}
         width="68rem"
         onClose={closeModals}
         themeName={TscThemeName.dawn}
         isLoading={state.isWorking || initialIsWorking}
         testId="edit-quiz-modal"
      >
         <StyledIFrame src={EditUrl} />
      </BasicModalPortal>
   );

   const removeQuizConfirmationModalBody: React.JSX.Element = (
      <>
         {state.isWorking && <AlertModalSpinner />}
         <div>
            {state.hasError && (
               <StyledWarning>
                  {state.errorText}
               </StyledWarning>
            )}
            <span>
               {t('quizzing.removeQuizConfirmationModalText', {quizTitle: Title})}
            </span>
         </div>
      </>
   );

   const removeQuizConfirmationModal = (
      <AlertModalPortal
         title={t('quizzing.removeQuizConfirmationModalTitle')}
         modalWidth={cssConstants.defaultModalWidth}
         onClose={closeModals}
         onConfirm={removeQuiz}
         onDismiss={closeModals}
         confirmContent={t('quizzing.removeQuizButton')}
         confirmButtonType={TscButtonType.destructive}
         disableConfirmButton={state.isWorking}
         dismissContent={t('general.cancel')}
         dismissButtonType={TscButtonType.secondary}
      >
         {removeQuizConfirmationModalBody}
      </AlertModalPortal>
   );

   return (
      <TabContentWrapper
         showSpinner={state.isWorking || initialIsWorking}
         showError={false}
      >
         <QuizRetirementAnnouncementContainer
            data-test-id="retirement-announcement-container"
            theme={theme}
         >
            <div>
               <NewQuizRemoveMessage>
                  {t('quizzing.removeNewQuizMessage')}
               </NewQuizRemoveMessage>
               {t('quizzing.retirementMessage')}
            </div>
            <ControlButton
               title={t('quizzing.retirementLinkAriaLabel')}
               href={Constants.navigation.quizRetirementLink}
               label={t('quizzing.retirementLinkText')}
               aria={{'aria-label': t('quizzing.retirementLinkAriaLabel')}}
               buttonType={TscButtonType.floating}
               themeName={theme}
               openLinkInNewTab={true}
            />
         </QuizRetirementAnnouncementContainer>
         {state.hasError && (
            <StyledWarning>
               {state.errorText}
            </StyledWarning>
         )}
         {state.currentDisplay === displayStates.initial && initialDisplay}
         {state.currentDisplay === displayStates.loading && loadingDisplay}
         {state.currentDisplay === displayStates.emptyState && emptyStateDisplay}
         {state.currentDisplay === displayStates.showQuiz && quizDisplay}
         {state.currentModal === modalStates.viewResults && viewResultsModal}
         {state.currentModal === modalStates.edit && editQuizModal}
         {state.currentModal === modalStates.removeQuiz && removeQuizConfirmationModal}
      </TabContentWrapper>
   );
};

export interface IQuizzingTabState extends IWorkerState {
   errorText: string;
   currentDisplay: displayStates;
   currentModal: modalStates;
}

export interface IQuizzingTabProps {
   initialIsWorking: boolean;
   initialHasError: boolean;
}

export interface IStateMappedProps {
   CreateDate: string;
   HasBeenPublished: boolean;
   Id: string;
   Title: string;
   ResultsUrl: string;
   EditUrl: string;
   QuestionSets: object;
   Hash: string;
   IsEncoding: boolean;
   QuizId: string;
}

const mapStatesToProps = (
   {initialIsWorking, initialHasError}: IQuizzingTabProps,
   {CreateDate, HasBeenPublished, Id, Title, ResultsUrl, EditUrl, QuestionSets}: Partial<IStateMappedProps>,
   {Hash, IsEncoding, QuizId}: Partial<IStateMappedProps>
): IStateMappedProps & IQuizzingTabProps => ({initialIsWorking, initialHasError, CreateDate, HasBeenPublished, Id, Title, ResultsUrl, EditUrl, QuestionSets, Hash, IsEncoding, QuizId});

export default withMemoizedContexts(mapStatesToProps, quizDataStore, mediaStore)(QuizzingTabBase);
